// eslint-disable-next-line max-classes-per-file
import { Metamodel } from '../base/model';
import { IModel } from '../base/annotation';
import { Activity } from './activity';

import { format } from 'app/shared/utils/string.util';
import { Collection, Credential, CredentialTypeOptions, Owned, LogicAppsProperty, IViolation, shellStringResources } from 'shell-models-lib';

export class ExtensibleActivity extends Activity {
    public static readonly MODEL: IModel;

    @Metamodel.attribute({
        type: LogicAppsProperty.MODEL,
        collection: Collection.Array,
    })
    public properties?: LogicAppsProperty[] = [];

    public connection?: string;
    public path?: string = '';
    public method?: string = 'post';
    public connector: string = 'office365';
    public credentialType = CredentialTypeOptions.EmailAuth;
    public account?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_Credential,
        constraint: {
            required: true
        },
        reference: true,
        type: Credential.MODEL
    })
    public credential?: Credential;
    public isModifiedByAnotherUser: boolean = false;
    public isSignInFixed: boolean = false;

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ControlActivity';
    }

    protected static propertiesValidator(activity: ExtensibleActivity): IViolation[] {
        const result: IViolation[] = [];
        if (activity.isModifiedByAnotherUser && !activity.isSignInFixed) {
            result.push({
                context: activity,
                message: () => format(stringResources.TridentLogicAppsSignInRequired, activity.getActivityName(Metamodel.modelOf(activity).name))
            });
        }
        activity.propertiesIncludingChildren().forEach((property) => {
            const attribute = Metamodel.modelOf(property).attribute('value');
            if (property.required && !attribute.templateValue(property) && !property.value && !property.children.length) {
                result.push({
                    context: activity,
                    message: () => format(shellStringResources.RequiredValidatorMessageNoVowel, Metamodel.modelOf(activity).display[0], activity.name, '\'' + property.name + '\''),
                    attributes: [attribute]
                });
            }
            if ((property.loading || property.loadingChildren) && property.loadingMessage) {
                result.push({
                    context: activity,
                    message: () => format(stringResources.ExtensibleValidation, Metamodel.modelOf(activity).display[0], activity.name, property.loadingMessage),
                    attributes: [attribute]
                });
            }
        });
        return result;
    }

    public getActivityName(type: string): string {
        switch (type) {
            case TeamsActivity.MODEL.name:
                return stringResources.Teams;
            case EmailActivity.MODEL.name:
                return stringResources.Email;
            default:
                return '';
        }
    }

    public addProperty(parent?: LogicAppsProperty): LogicAppsProperty {
        const property = Owned.create<LogicAppsProperty>(LogicAppsProperty.MODEL, this);
        if (parent) {
            parent.children.push(property);
        } else {
            this.properties.push(property);
        }
        return property;
    }

    public filledPath(): string {
        let filledPath = this.path;
        this.properties.forEach((property) => {
            filledPath = filledPath.replace('{' + property.name + '}', property.value);
        });
        return filledPath;
    }

    public propertiesIncludingChildren(array: LogicAppsProperty[] = this.properties): LogicAppsProperty[] {
        let result: LogicAppsProperty[] = [];
        array.forEach((item) => {
            result.push(item);
            if (item.children) {
                result = result.concat(this.propertiesIncludingChildren(item.children));
            }
        });
        return result;
    }
}

@Metamodel.model({
    name: 'Office365Outlook',
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.EmailActivityLabel),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.EmailActivityLabel)
    ],
    referable: true,
    validator:  ExtensibleActivity.propertiesValidator
})
export class EmailActivity extends ExtensibleActivity {
    public connector = 'office365';
    public credentialType = CredentialTypeOptions.EmailAuth;
}

@Metamodel.model({
    name: 'Teams',
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.TeamsActivityLabel),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.TeamsActivityLabel)
    ],
    referable: true,
    validator: ExtensibleActivity.propertiesValidator
})
export class TeamsActivity extends ExtensibleActivity {
    public connector = 'teams';
    public credentialType = CredentialTypeOptions.TeamsAuth;
}

@Metamodel.model({
    name: 'BlobStorage',
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.AzureBlobStorage_DisplayText),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.AzureBlobStorage_DisplayText)
    ],
    referable: true,
    validator: ExtensibleActivity.propertiesValidator
})
export class AzureBlobStorageActivity extends ExtensibleActivity {
    public connector = 'azureblob';
    public credentialType = CredentialTypeOptions.AzureBlobAuth;
}

@Metamodel.model({
    name: 'AzureDevOps',
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.AzureDevopsActivityLabel),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.AzureDevopsActivityLabel)
    ],
    referable: true,
    validator: ExtensibleActivity.propertiesValidator
})
export class AzureDevOpsActivity extends ExtensibleActivity {
    public connector = 'visualstudioteamservices';
    public credentialType = CredentialTypeOptions.AzureDevopsAuth;
}

@Metamodel.model({
    name: 'InvokeADFpipeline',
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.InvokeADFPipelineActivityLabel),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.InvokeADFPipelineActivityLabel)
    ],
    referable: true,
    validator: ExtensibleActivity.propertiesValidator
})
export class InvokeADFPipelineActivity extends ExtensibleActivity {
    public connector = 'azuredatafactory';
    public credentialType = CredentialTypeOptions.AzureDataFactoryAuth;
}
